import type { Viewport } from '../../../engine/hex/camera2d';
import type { MapModel } from '../../../engine/map/mapModel';
import type { HexMapState } from '../../../engine/render/renderer';
import { t } from '../../../i18n/translations';
import type { IconRegistry } from '../assets/IconRegistry';
import { overlayTokens } from '../styles/tokens';
import { buildActionBarVM, buildActionMenus, type TextMeasurer } from './buildActionBarVM';
import { buildDismissibles } from './buildDismissibles';
import type { InfoPanelVM, InstructionsBarVM, OverlayVM } from './types';
import { isMobileViewport } from '../../../engine/render/modalLayout';

export type BuildOverlayArgs = {
    model: MapModel;
    state: HexMapState;
    viewport: Viewport;
    zoom: number;
    icons: IconRegistry;
    measureText: TextMeasurer;
};

export function buildOverlayVM({
    model,
    state,
    viewport,
    zoom,
    icons,
    measureText,
}: BuildOverlayArgs): OverlayVM {
    const actionBar = buildActionBarVM({ state, viewport, icons, measureText });

    const actionMenus = buildActionMenus(state, viewport, measureText, actionBar.rect);
    const infoPanel = buildInfoPanelVM(
        model,
        state,
        actionBar.rect,
        zoom,
        isMobileViewport(viewport),
    );
    const instructions = buildInstructionsVM(state, viewport);
    const dismissibles = buildDismissibles(
        state,
        measureText,
        infoPanel.rect.y + infoPanel.rect.h + 10,
    );

    return {
        actionBar,
        actionMenus,
        infoPanel,
        instructions,
        notifications: dismissibles.notifications,
        alerts: dismissibles.alerts,
    };
}

function buildInfoPanelVM(
    model: MapModel,
    state: HexMapState,
    actionBarRect: { y: number; h: number },
    zoom: number,
    isMobile: boolean,
): InfoPanelVM {
    const { infoPanel } = overlayTokens.layout;
    const lang = state.config.ui.language;
    const infoY = actionBarRect.y + actionBarRect.h + infoPanel.yOffset;
    if (isMobile) {
        return { rect: { x: infoPanel.x, y: infoY, w: infoPanel.w, h: 0 }, lines: [] };
    }
    const rect = { x: infoPanel.x, y: infoY, w: infoPanel.w, h: infoPanel.h };
    const activeLabel = state.activeModalId || t(lang, 'overlay.none');
    return {
        rect,
        lines: [
            { text: t(lang, 'overlay.hexCount', model.tiles.length), offsetY: 8 },
            { text: t(lang, 'overlay.zoom', zoom), offsetY: 24 },
            { text: t(lang, 'overlay.selected', activeLabel), offsetY: 40 },
        ],
    };
}

function buildInstructionsVM(state: HexMapState, viewport: Viewport): InstructionsBarVM {
    const { instructions } = overlayTokens.layout;
    const lang = state.config.ui.language;
    return {
        rect: {
            x: instructions.x,
            y: viewport.height - instructions.marginBottom,
            w: instructions.w,
            h: instructions.h,
        },
        text: t(lang, 'overlay.instructions'),
    };
}
